import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Icon } from "./icon";
import { cx } from "@/utils/cx";

/**
 * Gymkee 2027 select, bare variant (select 1.8; sheet DS05).
 *
 * A value inside a row, not a field in a form: no ground and no hairline, the value and the 20
 * chevron on a 32 trigger inside a 44 target, as wide as its longest option. The menu is select's
 * own: a popover the trigger's width from 640, the sheet from the foot of the screen with 52 rows
 * and a grab handle below it. The chosen option is tinted with the check and stays tinted under
 * the pointer.
 *
 * Keyboard and roles are select's: the trigger is a combobox and keeps focus while the menu is
 * open (the highlight is aria-activedescendant), the menu is a listbox of options. Enter, Space
 * or Down opens; Up and Down move and wrap; Home and End jump to the ends; a letter jumps to the
 * next option starting with it; Enter chooses and closes; Escape closes and keeps the value; Tab
 * closes and moves on.
 */

export interface BareSelectOption {
  value: string;
  label: string;
}

export interface BareSelectProps {
  /** The control's spoken name, from an i18n key. */
  label: string;
  value: string;
  options: BareSelectOption[];
  onChange: (value: string) => void;
  className?: string;
}

const SHEET_QUERY = "(max-width: 639px)";

function useSheet(): boolean {
  const [sheet, setSheet] = useState(
    () => typeof window !== "undefined" && window.matchMedia(SHEET_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(SHEET_QUERY);
    const update = () => setSheet(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return sheet;
}

export function BareSelect({ label, value, options, onChange, className }: BareSelectProps) {
  const [open, setOpen] = useState(false);
  // The menu stays mounted while it fades out (select 1.8 motion: close is opacity over fast on
  // exit); `shown` drops when its exit animation ends, or at once under reduced motion.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) {
      setShown(true);
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) setShown(false);
  }, [open]);
  const onExitEnd = () => {
    if (!open) setShown(false);
  };
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const sheet = useSheet();
  const count = options.length;
  const current = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const optionId = (index: number) => `${listId}-option-${index}`;

  const openAt = (index: number) => {
    setHighlight(index);
    setOpen(true);
  };

  const choose = (index: number) => {
    const option = options[index];
    setOpen(false);
    triggerRef.current?.focus();
    if (option && option.value !== value) onChange(option.value);
  };

  useEffect(() => {
    if (!open || sheet) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, sheet]);

  const letterJump = (key: string, from: number): number | null => {
    const letter = key.toLocaleLowerCase();
    for (let step = 1; step <= count; step += 1) {
      const index = (from + step) % count;
      if (options[index].label.toLocaleLowerCase().startsWith(letter)) return index;
    }
    return null;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const printable =
      event.key.length === 1 && /\S/.test(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        openAt(current);
      } else if (printable) {
        const index = letterJump(event.key, current);
        if (index !== null) {
          event.preventDefault();
          openAt(index);
        }
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlight((index) => (index + 1) % count);
        return;
      case "ArrowUp":
        event.preventDefault();
        setHighlight((index) => (index - 1 + count) % count);
        return;
      case "Home":
        event.preventDefault();
        setHighlight(0);
        return;
      case "End":
        event.preventDefault();
        setHighlight(count - 1);
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(highlight);
        return;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        return;
      case "Tab":
        setOpen(false);
        return;
      default:
        if (printable) {
          const index = letterJump(event.key, highlight);
          if (index !== null) {
            event.preventDefault();
            setHighlight(index);
          }
        }
    }
  };

  const list = (
    <ul
      id={listId}
      role="listbox"
      aria-label={label}
      data-state={open ? "open" : "closed"}
      onAnimationEnd={sheet ? undefined : onExitEnd}
      className={
        sheet
          ? "ds-sheet-list"
          : "ds-card ds-overlay p-gk-1 mt-gk-1 absolute inset-x-0 top-full z-20 flex flex-col"
      }
    >
      {options.map((option, index) => {
        const chosen = option.value === value;
        return (
          <li
            key={option.value}
            id={optionId(index)}
            role="option"
            lang={option.value}
            aria-selected={chosen}
            data-highlighted={index === highlight ? "" : undefined}
            data-state={chosen ? "checked" : undefined}
            className={cx("ds-menu-item", sheet && "ds-sheet-row")}
            // Options are reached by the arrows on the combobox (aria-activedescendant); tabIndex -1
            // keeps them out of the tab order, and a press never takes focus from the trigger.
            tabIndex={-1}
            onMouseDown={(event) => event.preventDefault()}
            onPointerMove={() => setHighlight(index)}
            onClick={() => choose(index)}
          >
            <span className="flex-1 text-start">{option.label}</span>
            {chosen && (
              <span aria-hidden="true" className="flex text-[var(--gk-text-orange-ink)]">
                <Icon name="check" size={20} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <span ref={rootRef} className={cx("ds-select-bare", className)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(highlight) : undefined}
        aria-label={label}
        className="ds-select-bare-input"
        onClick={() => (open ? setOpen(false) : openAt(current))}
        onKeyDown={onKeyDown}
        // A button fires its click on Space's keyup; the keydown already acted.
        onKeyUp={(event) => {
          if (event.key === " ") event.preventDefault();
        }}
      >
        {/* The trigger is as wide as its longest option: the labels are stacked invisibly. */}
        <span className="grid">
          {options.map((option) => (
            <span
              key={option.value}
              aria-hidden="true"
              className="ds-select-bare-measure invisible col-start-1 row-start-1 whitespace-nowrap"
            >
              {option.label}
            </span>
          ))}
          <span className="col-start-1 row-start-1 whitespace-nowrap">
            {options[current]?.label ?? value}
          </span>
        </span>
      </button>
      <span className="ds-select-bare-chevron" aria-hidden="true">
        <Icon name="chevron-down" size={20} />
      </span>
      {shown && !sheet && list}
      {shown && sheet && (
        <div
          className="ds-sheet-scrim ds-select-sheet-scrim"
          data-state={open ? "open" : "closed"}
          onAnimationEnd={(event) => {
            // the sheet's own slide-out keyframes end the exit; the scrim's fade does not
            if (event.animationName.endsWith("sheet-out")) onExitEnd();
          }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setOpen(false);
              triggerRef.current?.focus();
            }
          }}
        >
          <div className="ds-sheet">
            <span className="ds-sheet-handle" aria-hidden="true" />
            <p className="ds-sheet-title" aria-hidden="true">
              {label}
            </p>
            <div className="ds-select-sheet-body">{list}</div>
          </div>
        </div>
      )}
    </span>
  );
}
